import React from 'react';
import ConfirmModal from '../components/ConfirmModal';
import { useAppContext } from '../contexts/AppContext';
import { updatePassword, fetchAuthSession, signOut as amplifySignOut } from 'aws-amplify/auth';
import { ORDER_API_BASE } from '../constants/config';

// AI Lambda base URL (strip /story suffix from the full AI_API constant)
const AI_API_BASE = 'https://ndb4d1r8u9.execute-api.us-east-1.amazonaws.com/dev';

const VIDEO_MAX_FILES = 10;

const ORDER_STATUS_LABELS = {
  PENDING_PAYMENT: 'Awaiting payment',
  PAID: 'Paid — preparing to print',
  SUBMITTED_TO_LULU: 'Sent to printer',
  IN_PRODUCTION: 'Printing',
  SHIPPED: 'Shipped',
  LULU_ERROR: 'Print error',
  CANCELLED: 'Cancelled',
};
const ORDER_STATUS_EMOJI = {
  PENDING_PAYMENT: '⏳',
  PAID: '✅',
  SUBMITTED_TO_LULU: '🖨️',
  IN_PRODUCTION: '📖',
  SHIPPED: '📦',
  LULU_ERROR: '⚠️',
  CANCELLED: '🚫',
};

export default function AccountPage() {
  const {
    user, userId, showToast, saveAccountSettings, signOut,
    accountSection, setAccountSection, setActiveTab,
    accountName, setAccountName,
    stories, books, accountSettings,
    formatBytes, videoTotalBytes, videoFiles,
    mfaLoading, mfaEnabled, mfaStep, setMfaStep,
    mfaSetupUri, setMfaSetupUri, mfaVerifyCode, setMfaVerifyCode,
    mfaError, setMfaError,
    handleMfaSetup, handleMfaVerify, handleMfaDisable,
  } = useAppContext();

  // Order history state
  const [orderHistory, setOrderHistory] = React.useState([]);
  const [ordersLoading, setOrdersLoading] = React.useState(false);
  const [ordersLoaded, setOrdersLoaded] = React.useState(false);
  const [ordersError, setOrdersError] = React.useState(false);
  const [cancelStep, setCancelStep] = React.useState(0); // 0=idle, 1=first confirm, 2=final confirm
  const [deletionLoading, setDeletionLoading] = React.useState(false);
  const [deletionError, setDeletionError] = React.useState(null);

  // TB-081: in-session password change (Path A — updatePassword, no email round-trip)
  const [pwFormOpen, setPwFormOpen] = React.useState(false);
  const [pwCurrent, setPwCurrent] = React.useState('');
  const [pwNew, setPwNew] = React.useState('');
  const [pwConfirm, setPwConfirm] = React.useState('');
  const [pwLoading, setPwLoading] = React.useState(false);

  const closePwForm = () => {
    setPwFormOpen(false);
    setPwCurrent(''); setPwNew(''); setPwConfirm('');
  };

  const handleChangePassword = async () => {
    if (!pwCurrent || !pwNew || !pwConfirm) { showToast('Please fill in all three password fields.', 'error'); return; }
    if (pwNew !== pwConfirm) { showToast('New passwords don\'t match.', 'error'); return; }
    if (pwNew.length < 8) { showToast('New password must be at least 8 characters.', 'error'); return; }
    setPwLoading(true);
    try {
      await updatePassword({ oldPassword: pwCurrent, newPassword: pwNew });
      showToast('Password updated.', 'success');
      closePwForm();
    } catch (err) {
      if (err.name === 'NotAuthorizedException') {
        showToast('Current password is incorrect.', 'error');
      } else if (err.name === 'InvalidPasswordException') {
        showToast(err.message || 'New password doesn\'t meet requirements.', 'error');
      } else if (err.name === 'LimitExceededException') {
        showToast('Too many attempts. Please wait a few minutes and try again.', 'error');
      } else {
        showToast('Something went wrong updating your password. Please try again.', 'error');
      }
    }
    setPwLoading(false);
  };

  // Fetch order history when Plan & Usage tab is opened
  React.useEffect(() => {
    if ((accountSection !== 'usage' && accountSection !== 'orders') || ordersLoaded || ordersLoading) return;
    const fetchOrders = async () => {
      setOrdersLoading(true);
      setOrdersError(false);
      try {
        let token = null;
        try {
          const session = await fetchAuthSession();
          token = session.tokens?.idToken?.toString() || null;
        } catch (sessionErr) {
          console.warn('[Auth] Could not retrieve session for order list:', sessionErr);
        }
        const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
        const resp = await fetch(`${ORDER_API_BASE}/order/list/${encodeURIComponent(userId)}`, { headers });
        if (resp.ok) {
          const data = await resp.json();
          setOrderHistory(data.orders || []);
        } else {
          setOrdersError(true);
        }
      } catch (err) {
        console.warn('Failed to load order history:', err);
        setOrdersError(true);
      }
      setOrdersLoading(false);
      // Mark loaded either way to avoid a refetch loop; on error the UI offers a retry
      // (which resets ordersLoaded) instead of showing a permanent silent "No orders".
      setOrdersLoaded(true);
    };
    fetchOrders();
  }, [accountSection, userId, ordersLoaded, ordersLoading]);

  // Compute book stats
  const personalBooks = (books || []).filter(b => b.bookType !== 'GIFT');
  const giftBooks = (books || []).filter(b => b.bookType === 'GIFT');
  const totalChapters = (books || []).reduce((sum, b) => sum + (b.pages?.length || 0), 0);
  const totalPhotos = (books || []).reduce((sum, b) => sum + (b.pages || []).filter(p => p.photoPath).length, 0);
  const totalEnhanced = (books || []).reduce((sum, b) => sum + (b.enhancedPhotos?.length || 0), 0);
  const freeEnhancementsUsed = accountSettings?.enhancementsUsed || 0;

  return (
    <>
    <div>
      <div className="page-header">
        <h2>Account</h2>
        <p>Your profile, plan, and security settings.</p>
      </div>

      {/* Tab bar */}
      <div className="account-tab-bar" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2px', marginBottom: '24px', background: '#ffffff', border: '1px solid rgba(15,23,42,0.22)', borderRadius: '8px', padding: '4px' }}>
        {[
          { id: 'orders',  label: 'Orders' },
          { id: 'profile', label: 'Profile' },
          { id: 'usage',   label: 'Credits & Limits' },
          { id: 'security', label: 'Security' },
        ].map(s => (
          <button key={s.id} className="account-tab-btn" onClick={() => setAccountSection(s.id)}
            style={{ minWidth: 0, padding: '14px 8px', background: accountSection === s.id ? 'rgba(15,23,42,0.08)' : 'none', border: 'none', borderRadius: '6px', color: accountSection === s.id ? '#0f172a' : '#7a6b5a', fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '1px', cursor: 'pointer', transition: 'all 0.2s', borderBottom: accountSection === s.id ? '2px solid #0f172a' : '2px solid transparent', fontWeight: accountSection === s.id ? 600 : 400 }}>
            {s.label}
          </button>
        ))}
      </div>

      {/* Sign Out — reachable on every device (the mobile nav has no sidebar) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '24px' }}>
        <button onClick={() => signOut && signOut()}
          style={{ background: 'none', border: '1px solid rgba(15,23,42,0.18)', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontFamily: "'Lora', serif", fontSize: '0.95rem', letterSpacing: '1px', color: '#3c4653', transition: 'all 0.2s' }}>
          Sign Out
        </button>
      </div>

      {/* ═══ ORDERS TAB ═══ */}
      {accountSection === 'orders' && (
        <div>
          <div className="card">
            <h3 style={{ marginBottom: '16px' }}>Order History</h3>
            {ordersLoading ? (
              <p style={{ color: '#3c4653', fontStyle: 'italic' }}>Loading orders...</p>
            ) : ordersError ? (
              <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                <p style={{ fontSize: '1.05rem', color: '#3c4653', fontFamily: "'Lora', serif", marginBottom: 6 }}>We couldn't load your orders</p>
                <p style={{ fontSize: '0.9rem', color: '#3c4653', marginBottom: 14 }}>This is usually a connection hiccup, not a problem with your orders.</p>
                <button onClick={() => { setOrdersError(false); setOrdersLoaded(false); }}
                  style={{ padding: '10px 22px', borderRadius: 9999, border: 'none', background: 'linear-gradient(135deg,#F0A24C,#D2761F)', color: '#fff', fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer' }}>
                  Try again
                </button>
              </div>
            ) : orderHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                <p style={{ fontSize: '1.05rem', color: '#3c4653', fontFamily: "'Lora', serif", marginBottom: 6 }}>No orders yet</p>
                <p style={{ fontSize: '0.9rem', color: '#3c4653', marginBottom: 14 }}>When you order a printed book, it will appear here with tracking and status updates.</p>
                {setActiveTab && (
                  <button onClick={() => setActiveTab('book')}
                    style={{ padding: '10px 22px', borderRadius: 9999, border: '1.5px solid rgba(15,23,42,0.16)', background: '#fff', color: '#3a2f22', fontFamily: "'Nunito', sans-serif", fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}>
                    Go to my book →
                  </button>
                )}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {orderHistory.map(order => (
                  <div key={order.orderId} style={{
                    padding: '16px 18px', borderRadius: 10,
                    border: '1px solid rgba(15,23,42,0.1)',
                    background: order.status === 'SHIPPED' ? 'rgba(90,122,74,0.04)' : order.status === 'LULU_ERROR' ? 'rgba(192,57,43,0.04)' : '#fff',
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                      <div style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', fontWeight: 600, color: '#0f172a' }}>
                        {order.bookTitle || 'My Book'}
                      </div>
                      <span style={{
                        padding: '3px 10px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 600,
                        letterSpacing: '0.5px', whiteSpace: 'nowrap', flexShrink: 0,
                        background: order.status === 'SHIPPED' ? 'rgba(90,122,74,0.12)' : order.status === 'LULU_ERROR' ? 'rgba(192,57,43,0.1)' : 'rgba(15,23,42,0.06)',
                        color: order.status === 'SHIPPED' ? '#3a5a2a' : order.status === 'LULU_ERROR' ? '#8b2c1a' : '#3c4653',
                        border: `1px solid ${order.status === 'SHIPPED' ? 'rgba(90,122,74,0.25)' : order.status === 'LULU_ERROR' ? 'rgba(192,57,43,0.2)' : 'rgba(15,23,42,0.12)'}`,
                      }}>
                        {ORDER_STATUS_EMOJI[order.status] || ''} {ORDER_STATUS_LABELS[order.status] || order.status}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: '0.88rem', color: '#3c4653' }}>
                      {order.createdAt && (
                        <span>Ordered {new Date(order.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                      )}
                      {order.amount && (
                        <span>${(order.amount / 100).toFixed(2)}</span>
                      )}
                      {order.shippingAddress && (
                        <span>Ship to {order.shippingAddress.city}{order.shippingAddress.stateCode ? `, ${order.shippingAddress.stateCode}` : ''}</span>
                      )}
                      {order.trackingId && (
                        order.trackingUrl
                          ? <a href={order.trackingUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#5a7a4a', textDecoration: 'underline' }}>Track package</a>
                          : <span>Tracking: {order.trackingId}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══ PROFILE TAB ═══ */}
      {accountSection === 'profile' && (
        <div>
          {/* Your Info */}
          <div className="card">
            <h3 style={{ marginBottom: '20px' }}>Your Info</h3>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#3c4653', textTransform: 'uppercase', marginBottom: '8px' }}>Display Name</label>
              <input className="inp" value={accountName} onChange={e => setAccountName(e.target.value)} placeholder="Your name" />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#3c4653', textTransform: 'uppercase', marginBottom: '8px' }}>Email</label>
              <input className="inp" value={user?.signInDetails?.loginId || user?.username || ''} disabled style={{ opacity: 0.5, cursor: 'not-allowed' }} />
              <p style={{ color: '#3c4653', fontSize: '1.05rem', marginTop: '6px', fontStyle: 'italic' }}>Email cannot be changed here. Contact support@taleboundbook.com if needed.</p>
            </div>
            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#3c4653', textTransform: 'uppercase', marginBottom: '8px' }}>Password</label>
              {!pwFormOpen ? (
                <button className="btn btn-ghost" style={{ fontSize: '1rem' }} onClick={() => setPwFormOpen(true)}>
                  Change Password
                </button>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: 420 }}>
                  <input className="inp" type="password" autoComplete="current-password" placeholder="Current password" value={pwCurrent} onChange={e => setPwCurrent(e.target.value)} />
                  <input className="inp" type="password" autoComplete="new-password" placeholder="New password (at least 8 characters)" value={pwNew} onChange={e => setPwNew(e.target.value)} />
                  <input className="inp" type="password" autoComplete="new-password" placeholder="Confirm new password" value={pwConfirm} onChange={e => setPwConfirm(e.target.value)} />
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button className="btn-primary" disabled={pwLoading} onClick={handleChangePassword}>
                      {pwLoading ? 'Updating...' : 'Update Password'}
                    </button>
                    <button className="btn-secondary" disabled={pwLoading} onClick={closePwForm}>
                      Cancel
                    </button>
                  </div>
                  <p style={{ color: '#3c4653', fontSize: '1.05rem', fontStyle: 'italic', margin: 0 }}>
                    Forgot your current password? Sign out and use "Forgot your password?" on the sign-in screen.
                  </p>
                </div>
              )}
            </div>
            <button className="btn-primary" onClick={() => { saveAccountSettings({ displayName: accountName }); showToast('Profile saved.', 'success'); }}>
              Save Profile
            </button>
          </div>

        </div>
      )}

      {/* ═══ PLAN & USAGE TAB ═══ */}
      {accountSection === 'usage' && (
        <div>
          {/* Current Plan */}
          <div className="card" style={{ marginBottom: '8px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                <h3 style={{ marginBottom: '6px' }}>Current Plan</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{ fontFamily: "'Lora', serif", fontSize: '1.2rem', color: '#0f172a', letterSpacing: '2px' }}>FREE</span>
                  <span style={{ padding: '3px 12px', borderRadius: '20px', background: 'rgba(15,23,42,0.22)', border: '1px solid rgba(15,23,42,0.25)', color: '#0f172a', fontSize: '1.05rem', fontFamily: "'Lora', serif", letterSpacing: '1px' }}>ACTIVE</span>
                </div>
                <p style={{ color: '#3c4653', fontSize: '1.05rem', lineHeight: 1.65 }}>
                  Includes unlimited stories, 10 photo enhancements, and up to 2GB video storage.
                </p>
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: '8px', background: 'linear-gradient(135deg, rgba(15,23,42,0.05) 0%, rgba(140,80,180,0.06) 100%)', border: '1px solid rgba(15,23,42,0.6)' }}>
            <h3 style={{ marginBottom: '8px', color: '#0f172a' }}>Talebound Pro — Coming Soon</h3>
            <p style={{ color: '#0f172a', fontSize: '1rem', lineHeight: 1.75, marginBottom: '16px' }}>
              Additional video storage, priority support, custom delivery emails, and family access for multiple accounts.
            </p>
            <button className="btn-secondary" onClick={() => showToast('Pro plan coming soon. We\'ll notify you when it\'s available.', 'info')}>
              Notify Me When Pro Launches
            </button>
          </div>

          {/* Your Books */}
          <div className="card">
            <h3 style={{ marginBottom: '16px' }}>Your Books</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              {[
                { label: 'Personal Books', value: personalBooks.length },
                { label: 'Celebration Books', value: giftBooks.length },
                { label: 'Total Chapters', value: totalChapters },
                { label: 'Photos', value: totalPhotos },
              ].map(item => (
                <div key={item.label} style={{ padding: '14px 16px', background: 'rgba(15,23,42,0.025)', borderRadius: 10, border: '1px solid rgba(15,23,42,0.06)' }}>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: '1.5rem', color: '#0f172a', fontWeight: 600, marginBottom: 2 }}>{item.value}</div>
                  <div style={{ fontSize: '0.85rem', color: '#3c4653', letterSpacing: '0.5px' }}>{item.label}</div>
                </div>
              ))}
            </div>

            {/* Photo Enhancement Credits */}
            <div style={{ padding: '14px 16px', background: 'rgba(201,168,76,0.06)', borderRadius: 10, border: '1px solid rgba(201,168,76,0.15)', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: '0.95rem', fontWeight: 600, color: '#0f172a', fontFamily: "'Lora', serif" }}>Photo Enhancement</span>
                <span style={{ fontSize: '0.85rem', color: '#3c4653' }}>{totalEnhanced} photo{totalEnhanced !== 1 ? 's' : ''} enhanced</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  flex: 1, height: 6, borderRadius: 3, background: 'rgba(15,23,42,0.1)',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${(freeEnhancementsUsed / 10) * 100}%`, height: '100%',
                    background: freeEnhancementsUsed >= 10 ? '#3c4653' : '#7c2d12',
                    borderRadius: 3, transition: 'width 0.3s',
                  }} />
                </div>
                <span style={{ fontSize: '0.82rem', color: '#3c4653', whiteSpace: 'nowrap' }}>
                  {Math.max(0, 10 - freeEnhancementsUsed)} of 10 remaining
                </span>
              </div>
            </div>
          </div>

          {/* Usage */}
          <div className="card">
            <h3 style={{ marginBottom: '16px' }}>Usage</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
              {/* TB-082: Time Capsules usage row removed from launch scope */}
              {[
                { label: 'Stories', value: stories.length },
                { label: 'Video Storage', value: formatBytes(videoTotalBytes), max: '2 GB' },
                { label: 'Videos', value: videoFiles.length, max: VIDEO_MAX_FILES },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: '1px solid rgba(15,23,42,0.07)' }}>
                  <span style={{ color: '#0f172a', fontSize: '1rem' }}>{item.label}</span>
                  <span style={{ fontFamily: "'Lora', serif", fontSize: '1rem', color: '#0f172a' }}>
                    {item.value}{item.max ? ` / ${item.max}` : ''}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Cancel Account */}
          <div className="card">
            <div style={{ borderTop: '1px solid rgba(255,100,100,0.15)', paddingTop: '24px' }}>
              <h3 style={{ color: 'rgba(255,100,100,0.8)', marginBottom: '8px', fontSize: '1rem' }}>Cancel Account</h3>
              <p style={{ color: '#3c4653', fontSize: '1.05rem', lineHeight: 1.65, marginBottom: '16px' }}>
                Cancelling your account will permanently delete all your stories, vault files, and videos. This cannot be undone.
              </p>
              <button
                className="btn"
                style={{ background: 'rgba(200,0,0,0.12)', border: '1px solid rgba(200,0,0,0.4)', color: '#fc8181', fontSize: '1rem' }}
                onClick={() => setCancelStep(1)}>
                Cancel My Account
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ SECURITY TAB ═══ */}
      {accountSection === 'security' && (
        <div>
          {/* Two-Factor Authentication */}
          <div className="card" style={{ marginBottom: '16px' }}>
            <h3 style={{ marginBottom: '8px' }}>Two-Factor Authentication</h3>
            <p style={{ color: '#3c4653', fontSize: '1.05rem', lineHeight: 1.7, marginBottom: '24px', fontStyle: 'italic' }}>
              Add an extra layer of protection to your account. When enabled, you'll need your phone to sign in — even if someone has your password.
            </p>

            {mfaLoading ? (
              <p style={{ color: '#3c4653' }}>Checking security status...</p>
            ) : mfaEnabled && mfaStep !== 'setup' ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '16px 20px', background: 'rgba(104,211,145,0.08)', border: '1px solid rgba(104,211,145,0.25)', borderRadius: '8px', marginBottom: '20px' }}>
                  <span style={{ fontSize: '1.4rem' }}></span>
                  <div>
                    <div style={{ color: '#68d391', fontWeight: 600, marginBottom: '2px' }}>Two-factor authentication is enabled</div>
                    <div style={{ color: '#3c4653', fontSize: '1rem' }}>Your account is protected with an authenticator app.</div>
                  </div>
                </div>
                <button className="btn btn-ghost" style={{ fontSize: '1rem', color: '#fc8181', borderColor: 'rgba(252,129,129,0.3)' }} onClick={handleMfaDisable}>
                  Disable Two-Factor Authentication
                </button>
              </div>
            ) : mfaStep === 'idle' ? (
              <div>
                <div style={{ padding: '20px 24px', background: 'rgba(15,23,42,0.04)', border: '1px solid rgba(15,23,42,0.6)', borderRadius: '10px', marginBottom: '20px' }}>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#0f172a', textTransform: 'uppercase', marginBottom: '12px' }}>What is this?</div>
                  <p style={{ color: '#0f172a', fontSize: '1.05rem', lineHeight: 1.8, marginBottom: '0' }}>
                    Two-factor authentication means that when you sign in, you'll enter your password <em>and</em> a temporary code from your phone. Even if someone guesses your password, they still can't get into your account without your phone in hand. It takes about 2 minutes to set up.
                  </p>
                </div>
                <div style={{ padding: '20px 24px', background: '#ffffff', border: '1px solid rgba(15,23,42,0.1)', borderRadius: '10px', marginBottom: '24px' }}>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#0f172a', textTransform: 'uppercase', marginBottom: '14px' }}>Before you start, you'll need a free app on your phone</div>
                  <p style={{ color: '#3c4653', fontSize: '1.05rem', lineHeight: 1.8, marginBottom: '16px' }}>
                    Download one of these free apps from your phone's app store. You only need one:
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                    {[
                      { name: 'Google Authenticator', note: 'Most popular — search "Google Authenticator" in your App Store or Google Play', icon: '' },
                      { name: 'Microsoft Authenticator', note: 'Great if you already use Microsoft products', icon: '' },
                      { name: 'Authy', note: 'Easiest to use — backs up automatically if you get a new phone', icon: '' },
                    ].map(app => (
                      <div key={app.name} style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', padding: '14px 18px', background: '#ffffff', border: '1px solid rgba(15,23,42,0.08)', borderRadius: '8px' }}>
                        <span style={{ fontSize: '1.4rem', flexShrink: 0, marginTop: '2px' }}>{app.icon}</span>
                        <div>
                          <div style={{ color: '#0f172a', fontSize: '1.05rem', fontWeight: 500, marginBottom: '4px' }}>{app.name}</div>
                          <div style={{ color: '#3c4653', fontSize: '1.05rem', lineHeight: 1.6 }}>{app.note}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p style={{ color: '#3c4653', fontSize: '1rem', fontStyle: 'italic', lineHeight: 1.6 }}>
                    If you're not sure which to pick, go with <strong style={{ color: '#3c4653' }}>Google Authenticator</strong> — it's the simplest.
                  </p>
                </div>
                <button className="btn-primary" onClick={handleMfaSetup} style={{ width: '100%' }}>
                  I've installed the app — Set Up Two-Factor Authentication
                </button>
              </div>
            ) : mfaStep === 'setup' ? (
              <div>
                <div style={{ padding: '20px 24px', background: 'rgba(15,23,42,0.04)', border: '1px solid rgba(15,23,42,0.6)', borderRadius: '10px', marginBottom: '20px' }}>
                  <p style={{ color: '#0f172a', fontSize: '1.1rem', fontWeight: 600, marginBottom: '10px' }}>
                    Step 1: Open the authenticator app on your phone and scan this square code.
                  </p>
                  <p style={{ color: '#0f172a', fontSize: '1rem', lineHeight: 1.7 }}>
                    In your authenticator app, look for a "+" button or "Add account" or "Scan QR code" option. Then point your phone's camera at the code below.
                  </p>
                </div>
                <div style={{ textAlign: 'center', padding: '24px', background: '#fff', borderRadius: '10px', marginBottom: '16px', display: 'inline-block' }}>
                  <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(mfaSetupUri)}`} alt="MFA QR Code" style={{ width: '200px', height: '200px' }} />
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <p style={{ color: '#3c4653', fontSize: '1rem', marginBottom: '8px' }}>Can't scan? Copy this key into your app manually:</p>
                  <div style={{ padding: '12px 16px', background: '#ffffff', border: '1px solid rgba(15,23,42,0.1)', borderRadius: '6px', fontFamily: 'monospace', fontSize: '1.05rem', color: '#3c4653', wordBreak: 'break-all', userSelect: 'all', cursor: 'text' }}>
                    {mfaSetupUri.split('secret=')[1]?.split('&')[0] || mfaSetupUri}
                  </div>
                </div>
                <div style={{ padding: '20px 24px', background: 'rgba(15,23,42,0.04)', border: '1px solid rgba(15,23,42,0.6)', borderRadius: '10px', marginBottom: '20px' }}>
                  <p style={{ color: '#0f172a', fontSize: '1.1rem', fontWeight: 600, marginBottom: '10px' }}>
                    Step 2: Type the 6-digit number from your authenticator app.
                  </p>
                  <p style={{ color: '#0f172a', fontSize: '1rem', lineHeight: 1.7 }}>
                    After scanning, your app will show a 6-digit number that changes every 30 seconds. Type that number below. Don't worry if it changes — just type the current one.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input className="inp" value={mfaVerifyCode} onChange={e => setMfaVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000"
                    style={{ maxWidth: '180px', textAlign: 'center', fontSize: '1.4rem', letterSpacing: '8px', fontFamily: 'monospace', marginBottom: 0 }}
                    onKeyDown={e => { if (e.key === 'Enter' && mfaVerifyCode.length === 6) handleMfaVerify(); }} />
                  <button className="btn-primary" onClick={handleMfaVerify} disabled={mfaVerifyCode.length !== 6}>
                    Verify & Enable
                  </button>
                  <button className="btn btn-ghost" onClick={() => { setMfaStep('idle'); setMfaSetupUri(''); setMfaVerifyCode(''); setMfaError(''); }}>
                    Cancel
                  </button>
                </div>
                <div style={{ marginTop: '24px', padding: '16px 20px', background: '#ffffff', border: '1px solid rgba(15,23,42,0.08)', borderRadius: '8px' }}>
                  <div style={{ fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: '2px', color: '#3c4653', textTransform: 'uppercase', marginBottom: '10px' }}>Having trouble?</div>
                  <div style={{ color: '#3c4653', fontSize: '1.05rem', lineHeight: 1.75 }}>
                    <strong style={{ color: '#3c4653' }}>The code didn't work:</strong> The code changes every 30 seconds. Make sure you're typing the most recent one shown in your app.<br /><br />
                    <strong style={{ color: '#3c4653' }}>I can't find the scan option:</strong> Open the authenticator app → look for a "+" button (usually at the bottom) → tap "Scan a QR code."<br /><br />
                    <strong style={{ color: '#3c4653' }}>My phone can't scan the code:</strong> Use the "Can't scan?" key above instead. In your app, choose "Enter a setup key" or "Manual entry" and paste the key.
                  </div>
                </div>
              </div>
            ) : mfaStep === 'done' ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '20px', background: 'rgba(104,211,145,0.08)', border: '1px solid rgba(104,211,145,0.25)', borderRadius: '8px' }}>
                <span style={{ fontSize: '1.6rem' }}>🎉</span>
                <div>
                  <div style={{ color: '#68d391', fontWeight: 600, fontSize: '1.05rem', marginBottom: '4px' }}>Two-factor authentication is now active!</div>
                  <div style={{ color: '#3c4653', fontSize: '1rem', lineHeight: 1.6 }}>Next time you sign in, you'll enter your password and then a code from your authenticator app. Keep the app on your phone — you'll need it each time you log in.</div>
                </div>
              </div>
            ) : null}

            {mfaError && (
              <div style={{ marginTop: '12px', padding: '12px 16px', background: 'rgba(252,129,129,0.08)', border: '1px solid rgba(252,129,129,0.25)', borderRadius: '8px', color: '#fc8181', fontSize: '1rem' }}>
                {mfaError}
              </div>
            )}
          </div>

          {/* TB-082: Release Settings card hidden — References recipients/capsules cut from launch scope */}

          {/* Security Info Cards */}
          <div className="card" style={{ textAlign: 'center', background: 'rgba(15,23,42,0.03)' }}>
            <div style={{ fontFamily: "'Lora', serif", fontSize: '1.3rem', fontStyle: 'italic', color: '#0f172a', lineHeight: 1.7, marginBottom: '12px' }}>
              "You're trusting us with the most personal words you'll ever write.<br />We don't take that lightly."
            </div>
            <div style={{ fontSize: '1rem', color: '#3c4653' }}>— The Talebound Team</div>
          </div>

          {[
            { icon: '', title: 'Your Data Is Encrypted — Always', body: 'Every story and file is encrypted using AES-256 encryption — the same standard used by banks, hospitals, and the U.S. military. Data is encrypted in transit (between your device and our servers via TLS/HTTPS) and at rest (while stored in the cloud). Even if someone physically accessed the servers, they would see nothing but unreadable encrypted blocks.' },
            { icon: '', title: 'Not Even We Can Read Your Stories', body: 'Talebound is designed so that no one on our team can browse, read, or access your individual stories, messages, or files. Your data is protected by owner-based access controls — the system only returns data that belongs to your account. There is no admin panel where we can look up a user\'s stories. There is no "backdoor." The only person who can see what\'s inside your archive is you — and anyone you explicitly choose to share with.' },
            { icon: '🏗', title: 'Each Vault Is Isolated', body: 'Your stories, files, and settings are stored in separate, isolated containers. User A cannot query, access, or even detect User B\'s data. This is enforced at the infrastructure level by AWS Identity and Access Management. Your uploaded documents and videos are stored in a private path unique to your identity — no other user or application can access it.' },
            { icon: '☁️', title: 'Built on AWS — The World\'s Most Trusted Cloud', body: 'Talebound runs entirely on Amazon Web Services, the same infrastructure trusted by Netflix, NASA, the Department of Defense, and the majority of Fortune 500 companies. Our infrastructure meets SOC 1/2/3, PCI DSS, HIPAA, FedRAMP, and ISO 27001 compliance standards.' },
            { icon: '', title: 'AI Privacy', body: 'When you use AI features, your words are sent to Anthropic\'s Claude AI via a secure API. Anthropic does not use API customer data to train their models — your stories are never fed into the AI\'s learning process. Talebound does not store your AI conversations. The chat exists only in your browser\'s memory and disappears when you navigate away. The only thing we save is the final story you choose to keep.' },
            { icon: '✋', title: 'What We Will Never Do', body: 'We will never sell your data — not to advertisers, data brokers, or anyone. We will never show you ads. We will never share your content with anyone you haven\'t explicitly designated. We will never read your stories — we built the system so we can\'t. We will never use your words to train AI.' },
            { icon: '', title: 'Protect Your Account', body: 'Use a strong, unique password and don\'t reuse it anywhere else. Keep your email secure — it\'s your recovery method. Be thoughtful about recipients — once content is released, it can\'t be retracted. Log out on shared devices.' },
            { icon: '💡', title: 'Why the Cloud Is Safer Than Your Computer', body: 'Home computers can be lost, stolen, damaged by water or fire, infected with malware, or simply fail. AWS data centers have physical security, redundant power, fire suppression, and data replicated across multiple facilities. Your vault endures — so your words will too. That\'s the whole point of Talebound.' },
          ].map((item) => (
            <div key={item.title} className="card" style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>
              <div style={{ fontSize: '1.6rem', flexShrink: 0, marginTop: '2px' }}>{item.icon}</div>
              <div>
                <h3 style={{ marginBottom: '12px', fontSize: '1.05rem' }}>{item.title}</h3>
                <p style={{ color: '#0f172a', fontSize: '1.05rem', lineHeight: 1.8, fontFamily: "'Lora', serif" }}>{item.body}</p>
              </div>
            </div>
          ))}

          <div style={{ textAlign: 'center', padding: '24px 0', color: '#3c4653', fontSize: '1rem', fontStyle: 'italic' }}>
            Questions? Contact support@taleboundbook.com
          </div>
        </div>
      )}
    </div>

    {cancelStep === 1 && (
      <ConfirmModal
        title="Cancel your account?"
        message="This will permanently delete all your stories, vault files, and videos. This cannot be undone."
        confirmLabel="Yes, cancel my account"
        cancelLabel="Keep my account"
        destructive
        onConfirm={() => setCancelStep(2)}
        onCancel={() => setCancelStep(0)}
      />
    )}

    {cancelStep === 2 && (
      <ConfirmModal
        title="This is your last chance."
        message="All your stories and videos will be deleted forever. Are you absolutely sure?"
        confirmLabel={deletionLoading ? 'Deleting...' : 'Delete everything'}
        cancelLabel="Go back"
        destructive
        onConfirm={async () => {
          if (deletionLoading) return;
          setDeletionError(null);
          setDeletionLoading(true);
          try {
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString() || null;
            if (!idToken) {
              setDeletionError('Could not verify your session. Please sign out and sign back in, then try again.');
              setDeletionLoading(false);
              return;
            }
            // identityId is needed for S3 prefix deletion of Amplify Storage files
            const identityId = session.identityId || null;

            const response = await fetch(`${AI_API_BASE}/user/delete`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`,
              },
              body: JSON.stringify({ identityId }),
            });

            if (!response.ok) {
              const errorBody = await response.json().catch(() => ({}));
              setDeletionError(
                errorBody.error || 'Account deletion failed. Please contact support@taleboundbook.com.'
              );
              setDeletionLoading(false);
              return; // Do NOT sign out — deletion failed
            }

            // Success: sign out and let the app return to the landing page
            await amplifySignOut();
          } catch (err) {
            console.error('[DeleteAccount] Error:', err);
            setDeletionError('Account deletion failed. Please contact support@taleboundbook.com.');
            setDeletionLoading(false);
          }
        }}
        onCancel={() => { setCancelStep(0); setDeletionError(null); }}
      />
    )}

    {deletionError && (
      <div style={{
        position: 'fixed', bottom: 32, left: '50%', transform: 'translateX(-50%)',
        background: '#fff0f0', border: '1.5px solid #e57373', borderRadius: 10,
        padding: '16px 24px', maxWidth: 480, width: '90%', zIndex: 9999,
        color: '#7b1919', fontSize: '0.97rem', boxShadow: '0 4px 24px rgba(0,0,0,0.13)',
        textAlign: 'center',
      }}>
        <strong>Deletion failed.</strong> {deletionError}
        <button
          onClick={() => setDeletionError(null)}
          style={{ marginLeft: 16, background: 'none', border: 'none', cursor: 'pointer', color: '#7b1919', fontWeight: 700, fontSize: '1rem' }}
        >
          Dismiss
        </button>
      </div>
    )}
    </>
  );
}
